/* Applications.tsx – FULL FILE (inline-pill status + filters + sorting incl. Date) */

import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Avatar,
  Tooltip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Skeleton from '@material-ui/lab/Skeleton';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
// import {Link} from 'react-router-dom';
import './Applications.scss';
// import {useHistory} from 'react-router-dom';
import NavigationService from 'services/NavigationService';

import {
  IFdApplicationType,
  ISupplierUserProfile,
  IApplicationOwnershipType,
} from 'constants/interfaces';
import {EApplicationStateType} from 'constants/index';
import {
  apiGetAllFdApplications,
  apiUpdateFdApplicationState,
  apiGetTeam,
  apiGetUserTraderApplications,
  apiCreateOrUpdateFDApplicationOwnership,
  apiGetSupportCustomers,
  apiInitiateFdApplication,
  apiGetFarmer,
} from 'constants/Api';
import {_TotalFundingAsk, formatDate} from 'constants/index';
import {lshFilterFdApplicationsForLinkedCustomers} from 'services/SupplierCustomerScope';

/* ---------- helper colours ---------- */
// const colourForPct = (p: number) =>
//   p < 50 ? '#D32F2F' : p < 75 ? '#F57C00' : '#388E3C';

/* ---------- status palette ---------- */
const STATUS_META: Record<
  EApplicationStateType,
  {label: string; colour: string}
> = {
  IN_PROGRESS: {label: 'In Progress', colour: '#5B8ED9'},
  PENDING_REVIEW: {label: 'Pending Review', colour: '#5B8ED9'},
  IN_REVIEW: {label: 'In Review', colour: '#5B8ED9'},
  SUBMITTED_TO_CREDIT: {label: 'Submitted to Credit', colour: '#F1A24A'},
  PENDING_SIGNATURES: {label: 'Pending Signatures', colour: '#F1A24A'},
  CONDITION_PRECEDENT: {label: 'Conditions Precedent', colour: '#F1A24A'},
  TERM_SHEET_SIGNATURE: {
    label: 'Term-Sheet Signature',
    colour: '#F1A24A',
  },
  PENDING_PAYOUT: {label: 'Pending Payout', colour: '#67B67A'},
  FINAL_STAGE: {label: 'Finalised', colour: '#67B67A'},
  DECLINED: {label: 'Declined', colour: '#E57373'},
};

/* ---------- compact chevron ---------- */
const SmallArrow = (p: any) => (
  <ArrowDropDownIcon {...p} style={{fontSize: 18}} />
);

/* ---------- numeric helpers ---------- */
const numericAsk = (a: IFdApplicationType) =>
  a.fundingAsk?.fundingRequestCosts?.reduce((s, c) => s + (c.cost || 0), 0) ??
  0;

/* ---------- in-table status dropdown ---------- */
type StatusProps = {appId: string; value: EApplicationStateType};

const StatusDropdown: React.FC<StatusProps> = ({appId, value}) => {
  const [state, setState] = useState(value);
  const [busy, setBusy] = useState(false);
  const meta = STATUS_META[state];

  const handleChange = async (e: React.ChangeEvent<{value: unknown}>) => {
    const next = e.target.value as EApplicationStateType;
    if (next === state) return;
    const prev = state;
    setState(next);
    setBusy(true);
    try {
      await apiUpdateFdApplicationState(appId, next);
    } catch (err) {
      console.error('status update failed', err);
      setState(prev);
      alert('Unable to update status, please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box position="relative" display="inline-block">
      <Select
        value={state}
        onChange={handleChange}
        IconComponent={SmallArrow}
        variant="outlined"
        disabled={busy}
        MenuProps={{
          anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
        }}
        style={{
          background: meta.colour,
          color: '#fff',
          borderRadius: 10,
          fontSize: 10,
          fontWeight: 600,
          height: 22,
          lineHeight: '18px',
          padding: '0 20px 0 8px',
          minWidth: 112,
          maxWidth: 148,
        }}
      >
        {Object.values(EApplicationStateType).map((s) => (
          <MenuItem key={s} value={s} style={{fontSize: 12}}>
            {STATUS_META[s].label}
          </MenuItem>
        ))}
      </Select>
      {busy && (
        <CircularProgress
          size={12}
          thickness={5}
          style={{position: 'absolute', top: 5, right: 5, color: '#fff'}}
        />
      )}
    </Box>
  );
};

/* ---------- ownership + progress cells ---------- */
const getInitials = (name?: string, surname?: string) => {
  const n = (name || '').trim();
  const s = (surname || '').trim();
  const a = n ? n[0] : '';
  const b = s ? s[0] : '';
  return (a + b || '').toUpperCase();
};

type OwnershipCellProps = {
  appId: string;
  ownerId?: string;
  reportingToId?: string;
  team: ISupplierUserProfile[];
  saving: boolean;
  onChange: (ownerId: string, reportingToId: string) => void;
  onSave: (ownerId: string, reportingToId: string) => Promise<void>;
};

const OwnershipCell: React.FC<OwnershipCellProps> = ({
  appId,
  ownerId = '',
  reportingToId = '',
  team,
  saving,
  onChange,
  onSave,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [localOwner, setLocalOwner] = useState(ownerId);
  const [localReportTo, setLocalReportTo] = useState(reportingToId);

  const findUser = (id?: string) =>
    team.find((t) => String(t.id) === String(id));
  const owner = findUser(ownerId);
  const reporter = findUser(reportingToId);

  const open = Boolean(anchor);

  const handleSave = async () => {
    if (!localOwner || !localReportTo) return;
    onChange(localOwner, localReportTo);
    await onSave(localOwner, localReportTo);
    setAnchor(null);
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      style={{gap: 8}}
    >
      <Box
        display="flex"
        alignItems="center"
        style={{cursor: 'pointer'}}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        <Tooltip
          title={
            owner ? `Owner: ${owner.name} ${owner.surname}` : 'Owner not set'
          }
        >
          <Avatar
            src={owner?.pictureUrl}
            style={{width: 24, height: 24, border: '2px solid #fff'}}
          >
            {getInitials(owner?.name, owner?.surname)}
          </Avatar>
        </Tooltip>
        <Tooltip
          title={
            reporter
              ? `Reporting to: ${reporter.name} ${reporter.surname}`
              : 'Reporting to not set'
          }
        >
          <Avatar
            src={reporter?.pictureUrl}
            style={{
              width: 24,
              height: 24,
              marginLeft: -8,
              border: '2px solid #fff',
            }}
          >
            {getInitials(reporter?.name, reporter?.surname)}
          </Avatar>
        </Tooltip>
      </Box>
      {saving && <CircularProgress size={14} thickness={5} />}
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
      >
        <Box
          className="ownership-popover"
          display="grid"
          onClick={(e) => e.stopPropagation()}
        >
          <Select
            className="ownership-select"
            value={localOwner}
            onChange={(e) => setLocalOwner(String((e.target as any).value))}
            displayEmpty
            variant="outlined"
          >
            <MenuItem value="">
              <em>Select owner</em>
            </MenuItem>
            {team.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name} {u.surname}
              </MenuItem>
            ))}
          </Select>
          <Select
            className="ownership-select"
            value={localReportTo}
            onChange={(e) => setLocalReportTo(String((e.target as any).value))}
            displayEmpty
            variant="outlined"
          >
            <MenuItem value="">
              <em>Reporting to</em>
            </MenuItem>
            {team.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name} {u.surname}
              </MenuItem>
            ))}
          </Select>
          <Box
            className="ownership-popover-actions"
            display="flex"
            justifyContent="flex-end"
          >
            <Button size="small" onClick={() => setAnchor(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

const ProgressCell = ({pct}: {pct: number}) => (
  <Box style={{width: 52, paddingTop: 4, paddingBottom: 4}}>
    <Typography variant="caption" style={{fontWeight: 600}}>
      {pct.toFixed(0)}%
    </Typography>
  </Box>
);

/* ---- sort helpers ---- */
type SortKey = '' | 'date' | 'ask' | 'score' | 'ha' | 'done' | 'out';
type SortDir = 'asc' | 'desc';
type SortDirState = '' | SortDir; // can hold ''

const nextDir: Record<SortDirState, SortDirState> = {
  '': 'asc',
  asc: 'desc',
  desc: '',
};

/* ---------- main list component ---------- */
const Applications = () => {
  const user = useSelector(
    (state: any) => state.user as ISupplierUserProfile | undefined,
  );

  /* raw data */
  const [apps, setApps] = useState<IFdApplicationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState<ISupplierUserProfile[]>([]);
  const [traderCompanyNames, setTraderCompanyNames] = useState<
    Record<string, string>
  >({});

  /* ownership selections per application */
  const [ownerMap, setOwnerMap] = useState<Record<string, string>>({});
  const [reportingMap, setReportingMap] = useState<Record<string, string>>({});
  const [savingMap, setSavingMap] = useState<Record<string, boolean>>({});

  /* search + filters */
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState<'ALL' | EApplicationStateType>('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [askMin, setAskMin] = useState<number | ''>('');
  const [askMax, setAskMax] = useState<number | ''>('');
  const [scoreMin, setScoreMin] = useState<number | ''>('');
  const [scoreMax, setScoreMax] = useState<number | ''>('');
  const [cropF, setCropF] = useState<'ALL' | string>('ALL');
  const [provinceF, setProvinceF] = useState<'ALL' | string>('ALL');
  const [ownerF, setOwnerF] = useState<'ALL' | string>('ALL');
  const [reportingF, setReportingF] = useState<'ALL' | string>('ALL');
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  // const [ownersF, setOwnersF] = useState<string[]>([]);
  // const [reportersF, setReportersF] = useState<string[]>([]);
  // const [showMore, setShowMore] = useState(false);

  /* sorting */
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDirState>('desc');

  /* initial load */
  useEffect(() => {
    setLoading(true);
    const isKhulaAdmin = !!user?.isKhulaAdmin;
    Promise.all([
      apiGetAllFdApplications(),
      // Khula admins see every application, not just ones linked to a
      // supplier's own customers, so skip fetching/scoping by them.
      isKhulaAdmin ? Promise.resolve([]) : apiGetSupportCustomers(),
    ])
      .then(([applications, customers]) => {
        setApps(
          isKhulaAdmin
            ? applications
            : lshFilterFdApplicationsForLinkedCustomers(
                applications,
                customers,
              ),
        );
      })
      .catch((error) => {
        console.error('Failed to load scoped funding applications', error);
        alert('Unable to load funding applications. Please try again later.');
        setApps([]);
      })
      .finally(() => setLoading(false));
  }, [user?.isKhulaAdmin]);

  /* team list for dropdowns */
  useEffect(() => {
    apiGetTeam()
      .then(setTeam)
      .catch((err) => console.error('Failed to load team', err));
  }, []);

  useEffect(() => {
    const missingExternalIds = Array.from(
      new Set(
        apps
          .map((app) => app.applyingUser?.externalId)
          .filter(Boolean)
          .map(String),
      ),
    ).filter((externalId) => traderCompanyNames[externalId] === undefined);

    if (!missingExternalIds.length) return;

    let active = true;

    Promise.all(
      missingExternalIds.map(async (externalId) => {
        try {
          const traderApplications = await apiGetUserTraderApplications(
            externalId,
          );
          const supplierApplication = traderApplications.find(
            (application) =>
              String(application?.tradeType || '').toUpperCase() === 'SUPPLIER',
          );

          return {
            externalId,
            companyName: String(
              supplierApplication?.company?.name || '',
            ).trim(),
          };
        } catch (error) {
          console.error('Failed to load trader company name', error);
          return {externalId, companyName: ''};
        }
      }),
    ).then((companyNames) => {
      if (!active) return;

      setTraderCompanyNames((current) => {
        const next = {...current};
        companyNames.forEach(({externalId, companyName}) => {
          next[externalId] = companyName;
        });
        return next;
      });
    });

    return () => {
      active = false;
    };
  }, [apps, traderCompanyNames]);

  /* unique crop list */
  const cropOptions = useMemo(() => {
    const set = new Set<string>();
    apps.forEach((a) =>
      a.productionPlans?.forEach((p) => p.crop?.name && set.add(p.crop.name)),
    );
    return Array.from(set).sort();
  }, [apps]);

  /* unique province list */
  const provinceOptions = useMemo(() => {
    const set = new Set<string>();
    apps.forEach((a) => {
      const prov = a.farm?.province;
      if (prov) set.add(prov);
    });
    return Array.from(set).sort();
  }, [apps]);

  /* ownership filter options (only current/latest assignments) */
  const ownerOptions = useMemo(() => {
    const map = new Map<string, ISupplierUserProfile>();
    apps.forEach((a) => {
      const latest = getLatestOwnership(a);
      const u = latest?.owner as ISupplierUserProfile | undefined;
      if (u?.id) map.set(String(u.id), u);
    });
    return Array.from(map.values()).sort((a, b) =>
      `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`),
    );
  }, [apps]);

  const reportingOptions = useMemo(() => {
    const map = new Map<string, ISupplierUserProfile>();
    apps.forEach((a) => {
      const latest = getLatestOwnership(a);
      const u = latest?.reportingTo as ISupplierUserProfile | undefined;
      if (u?.id) map.set(String(u.id), u);
    });
    return Array.from(map.values()).sort((a, b) =>
      `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`),
    );
  }, [apps]);

  /* helpers */
  const truncate = (s: string, n = 15) =>
    s && s.length > n ? `${s.slice(0, n)}…` : s;
  const isCompanyNameLoading = (a: IFdApplicationType) => {
    const externalId = a.applyingUser?.externalId;
    return Boolean(
      externalId && traderCompanyNames[String(externalId)] === undefined,
    );
  };
  const companyName = (a: IFdApplicationType) => {
    const externalId = a.applyingUser?.externalId;

    if (externalId) {
      return traderCompanyNames[String(externalId)] || '';
    }

    return a.company?.name || '';
  };
  const farmer = (a: IFdApplicationType) => {
    const full = a.applyingUser
      ? `${a.applyingUser.name} ${a.applyingUser.surname}`
      : '';
    return truncate(full);
  };
  const hectares = (a: IFdApplicationType) =>
    (a.farm?.ownedHectares || 0) + (a.farm?.numberOfLeasedHectares || 0);
  const cropList = (a: IFdApplicationType) =>
    (a.productionPlans || [])
      .map((p) => p.crop?.name)
      .filter(Boolean)
      .join(', ');
  function getLatestOwnership(
    a: IFdApplicationType,
  ): IApplicationOwnershipType | undefined {
    const list = a.applicationOwnerships || [];
    if (!list.length) return undefined;
    return list.reduce((latest, cur) => {
      const prevTs = new Date(latest.updatedAt || latest.createdAt).getTime();
      const curTs = new Date(cur.updatedAt || cur.createdAt).getTime();
      return curTs > prevTs ? (cur as any) : (latest as any);
    });
  }
  const donePct = (a: IFdApplicationType) =>
    a.completedSectionsPercentage ?? a.progress ?? 0;
  const outstanding = (a: IFdApplicationType) =>
    a.incompleteSections?.length ?? 0;

  /* filter then sort */
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const base = apps.filter((a) => {
      if (
        q &&
        !companyName(a).toLowerCase().includes(q) &&
        !(a.farm?.name || '').toLowerCase().includes(q) &&
        !farmer(a).toLowerCase().includes(q) &&
        !STATUS_META[a.applicationState].label.toLowerCase().includes(q)
      )
        return false;

      if (statusF !== 'ALL' && a.applicationState !== statusF) return false;

      if (
        cropF !== 'ALL' &&
        !a.productionPlans?.some((p) => p.crop?.name === cropF)
      )
        return false;

      if (provinceF !== 'ALL' && (a.farm?.province || '') !== provinceF)
        return false;

      if (
        ownerF !== 'ALL' &&
        !a.applicationOwnerships?.some(
          (o) => String(o.ownerId ?? o.owner?.id) === String(ownerF),
        )
      )
        return false;

      if (
        reportingF !== 'ALL' &&
        !a.applicationOwnerships?.some(
          (o) =>
            String((o as any).reportingToId ?? (o as any).reportingTo?.id) ===
            String(reportingF),
        )
      )
        return false;

      const created = new Date(a.createdAt);
      if (dateFrom && created < new Date(dateFrom)) return false;
      if (dateTo && created > new Date(dateTo)) return false;

      const ask = numericAsk(a);
      if (askMin !== '' && ask < askMin) return false;
      if (askMax !== '' && ask > askMax) return false;

      const score = a.applyingUser?.creditScore ?? 0;
      if (scoreMin !== '' && score < scoreMin) return false;
      if (scoreMax !== '' && score > scoreMax) return false;

      return true;
    });

    if (!sortKey) return base;

    const factor = sortDir === 'asc' ? 1 : -1;

    return [...base].sort((a, b) => {
      const vA =
        sortKey === 'date'
          ? new Date(a.createdAt).getTime()
          : sortKey === 'ask'
          ? numericAsk(a)
          : sortKey === 'score'
          ? a.applyingUser?.creditScore ?? 0
          : sortKey === 'ha'
          ? hectares(a)
          : sortKey === 'done'
          ? donePct(a)
          : outstanding(a);

      const vB =
        sortKey === 'date'
          ? new Date(b.createdAt).getTime()
          : sortKey === 'ask'
          ? numericAsk(b)
          : sortKey === 'score'
          ? b.applyingUser?.creditScore ?? 0
          : sortKey === 'ha'
          ? hectares(b)
          : sortKey === 'done'
          ? donePct(b)
          : outstanding(b);

      return (vA - vB) * factor;
    });
  }, [
    apps,
    search,
    statusF,
    cropF,
    provinceF,
    ownerF,
    reportingF,
    dateFrom,
    dateTo,
    askMin,
    askMax,
    scoreMin,
    scoreMax,
    sortKey,
    sortDir,
  ]);

  /* header click */
  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
    } else {
      const next = nextDir[sortDir];
      setSortDir(next);
      if (next === '') setSortKey('');
    }
  };

  const arrow = (key: SortKey) =>
    sortKey !== key
      ? ''
      : sortDir === 'asc'
      ? ' ▲'
      : sortDir === 'desc'
      ? ' ▼'
      : '';

  const activeAdvancedFilterCount = [
    ownerF !== 'ALL',
    reportingF !== 'ALL',
    Boolean(dateFrom),
    Boolean(dateTo),
    askMin !== '',
    askMax !== '',
    scoreMin !== '',
    scoreMax !== '',
  ].filter(Boolean).length;

  const clearAdvancedFilters = () => {
    setOwnerF('ALL');
    setReportingF('ALL');
    setDateFrom('');
    setDateTo('');
    setAskMin('');
    setAskMax('');
    setScoreMin('');
    setScoreMax('');
  };

  /* new-application drawer state */
  const [newAppDrawerOpen, setNewAppDrawerOpen] = useState(false);
  const [customers, setCustomers] = useState<
    Array<{
      id: string;
      externalUserId: string;
      farmerId: string;
      name: string;
      surname: string;
      email?: string;
      image?: string;
    }>
  >([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    externalUserId: string;
    farmerId: string;
    name: string;
    surname: string;
    email?: string;
  } | null>(null);
  const [creatingApp, setCreatingApp] = useState(false);
  const [newAppError, setNewAppError] = useState('');

  const openNewAppDrawer = () => {
    setNewAppDrawerOpen(true);
    setNewAppError('');
    setSelectedCustomer(null);
    if (!customers.length) {
      setCustomersLoading(true);
      apiGetSupportCustomers()
        .then((list: any[]) => {
          setCustomers(
            (list || []).map((c: any) => {
              const farmer = c.farmer || c;
              const externalUserId = String(
                farmer.userId ||
                  c.userId ||
                  farmer.externalId ||
                  c.externalId ||
                  '',
              );
              const farmerId = String(farmer.id || c.id || '');
              return {
                id: String(farmer?.userId || ''),
                externalUserId,
                farmerId,
                name: farmer.name || farmer.firstName || c.name || '',
                surname: farmer.surname || farmer.lastName || c.surname || '',
                email: farmer.email || c.email || '',
                image: farmer.image || farmer.pictureUrl || c.image || '',
              };
            }),
          );
        })
        .catch((err: any) => {
          console.error('Failed to load customers for new app', err);
        })
        .finally(() => setCustomersLoading(false));
    }
  };

  const handleCreateNewApplication = async () => {
    if (!selectedCustomer) return;
    setCreatingApp(true);
    setNewAppError('');
    try {
      const externalUserId = selectedCustomer.externalUserId;
      if (!externalUserId) {
        throw new Error('Unable to determine selected farmer user ID.');
      }
      let farmId: number | undefined;
      let companyId: number | undefined;
      console.log(selectedCustomer);
      if (selectedCustomer.email) {
        const farmer = await apiGetFarmer(selectedCustomer.email);
        farmId = farmer?.farm?.id;
        // The company may be stored under company, companyId, or similar depending on the farmer object
        companyId = (farmer as any)?.company?.id || (farmer as any)?.companyId;
      }
      if (!farmId) {
        throw new Error('Unable to determine farm ID for selected farmer.');
      }

      const applyingUserId = selectedCustomer.externalUserId; // Try using externalUserId for now

      const result = await apiInitiateFdApplication({
        applyingUser: Number(applyingUserId),
        farm: Number(farmId),
        company: Number(companyId),
      });
      const newAppId = result?.id;
      setNewAppDrawerOpen(false);
      if (newAppId) {
        NavigationService.navigate(`/applications/${newAppId}`);
      } else {
        // Reload list to show the new application
        const [applications, supportCustomers] = await Promise.all([
          apiGetAllFdApplications(),
          apiGetSupportCustomers(),
        ]);
        setApps(
          lshFilterFdApplicationsForLinkedCustomers(
            applications,
            supportCustomers,
          ),
        );
      }
    } catch (err: any) {
      console.error('Failed to create new application', err);
      setNewAppError(
        Array.isArray(err)
          ? err.join(', ')
          : err?.message ||
              'Failed to create a new application. Please try again.',
      );
    } finally {
      setCreatingApp(false);
    }
  };

  /* UI */
  return (
    <Box className="applications-container table-screen">
      <Box className="page-header-row">
        <Box>
          <Typography variant="h4" className="page-title">
            Funding Applications
          </Typography>
          <Typography variant="body1" className="page-subtitle">
            List of farmers who have applied for funding
          </Typography>
        </Box>
      </Box>

      {/* New Application Drawer */}
      <Drawer
        anchor="right"
        open={newAppDrawerOpen}
        onClose={() => setNewAppDrawerOpen(false)}
        classes={{paper: 'new-app-drawer'}}
      >
        <Box className="new-app-drawer__header">
          <Typography variant="h6">Start New Application</Typography>
          <IconButton
            aria-label="Close"
            onClick={() => setNewAppDrawerOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box className="new-app-drawer__content">
          <Typography
            variant="body2"
            color="textSecondary"
            style={{marginBottom: 16}}
          >
            Select a farmer to start a new funding application for.
          </Typography>
          {customersLoading ? (
            <Box display="flex" alignItems="center" style={{gap: 8}}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading farmers…</Typography>
            </Box>
          ) : (
            <Autocomplete
              options={customers}
              getOptionLabel={(c) =>
                `${c.name} ${c.surname}${c.email ? ` (${c.email})` : ''}`
              }
              value={selectedCustomer}
              onChange={(_, val) => setSelectedCustomer(val)}
              renderOption={(c) => (
                <Box display="flex" alignItems="center" style={{gap: 8}}>
                  <Avatar
                    src={(c as any).image}
                    style={{width: 28, height: 28}}
                  >
                    {(c.name?.[0] || '') + (c.surname?.[0] || '')}
                  </Avatar>
                  <span>
                    {c.name} {c.surname}
                    {c.email ? (
                      <span
                        style={{color: '#78716c', marginLeft: 4, fontSize: 12}}
                      >
                        {c.email}
                      </span>
                    ) : null}
                  </span>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  label="Search farmer"
                  placeholder="Type a name or email…"
                />
              )}
            />
          )}

          {newAppError ? (
            <Typography variant="body2" color="error" style={{marginTop: 12}}>
              {newAppError}
            </Typography>
          ) : null}
        </Box>
        <Box className="new-app-drawer__actions">
          <Button onClick={() => setNewAppDrawerOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedCustomer || creatingApp}
            onClick={handleCreateNewApplication}
          >
            {creatingApp ? (
              <>
                <CircularProgress
                  size={16}
                  thickness={5}
                  style={{marginRight: 8, color: '#fff'}}
                />
                Creating…
              </>
            ) : (
              'Create Application'
            )}
          </Button>
        </Box>
      </Drawer>

      <Box className="filter-toolbar">
        <TextField
          className="filter-control filter-search"
          placeholder="Search companies, farmers, or statuses..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="filter-search-icon" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          className="filter-control filter-select"
          select
          label="Province"
          value={provinceF}
          onChange={(e) => setProvinceF(e.target.value as any)}
          variant="outlined"
          size="small"
        >
          <MenuItem value="ALL">All provinces</MenuItem>
          {provinceOptions.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          className="filter-control filter-select"
          select
          label="Status"
          value={statusF}
          onChange={(e) => setStatusF(e.target.value as any)}
          variant="outlined"
          size="small"
        >
          <MenuItem value="ALL">All statuses</MenuItem>
          {Object.values(EApplicationStateType).map((s) => (
            <MenuItem key={s} value={s}>
              {STATUS_META[s].label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          className="filter-control filter-select"
          select
          label="Crop"
          value={cropF}
          onChange={(e) => setCropF(e.target.value as any)}
          variant="outlined"
          size="small"
        >
          <MenuItem value="ALL">All crops</MenuItem>
          {cropOptions.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>

        <Button
          className="advanced-filter-button"
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setAdvancedFiltersOpen(true)}
        >
          More filters
          {activeAdvancedFilterCount > 0 && (
            <span className="filter-count">{activeAdvancedFilterCount}</span>
          )}
        </Button>

        <Button
          id="start-new-application-btn"
          className="start-new-app-button"
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openNewAppDrawer}
        >
          Start new application
        </Button>
      </Box>

      <Drawer
        anchor="right"
        open={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
        classes={{paper: 'advanced-filters-drawer'}}
      >
        <Box className="advanced-filter-header">
          <Box>
            <Typography variant="h6">More filters</Typography>
          </Box>
          <IconButton
            aria-label="Close filters"
            onClick={() => setAdvancedFiltersOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        <Box className="advanced-filter-content">
          <Autocomplete
            options={ownerOptions}
            getOptionLabel={(u) => `${u.name} ${u.surname}`}
            value={
              ownerF === 'ALL'
                ? null
                : ownerOptions.find((u) => String(u.id) === String(ownerF)) ||
                  null
            }
            onChange={(_, val) => setOwnerF(val ? String(val.id) : 'ALL')}
            renderOption={(u) => (
              <Box display="flex" alignItems="center" style={{gap: 8}}>
                <Avatar src={u.pictureUrl} style={{width: 24, height: 24}}>
                  {getInitials(u.name, u.surname)}
                </Avatar>
                {u.name} {u.surname}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                className="filter-control"
                variant="outlined"
                size="small"
                label="Owner"
                placeholder="All owners"
              />
            )}
          />

          <Autocomplete
            options={reportingOptions}
            getOptionLabel={(u) => `${u.name} ${u.surname}`}
            value={
              reportingF === 'ALL'
                ? null
                : reportingOptions.find(
                    (u) => String(u.id) === String(reportingF),
                  ) || null
            }
            onChange={(_, val) => setReportingF(val ? String(val.id) : 'ALL')}
            renderOption={(u) => (
              <Box display="flex" alignItems="center" style={{gap: 8}}>
                <Avatar src={u.pictureUrl} style={{width: 24, height: 24}}>
                  {getInitials(u.name, u.surname)}
                </Avatar>
                {u.name} {u.surname}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                className="filter-control"
                variant="outlined"
                size="small"
                label="Reporting to"
                placeholder="All reporting to"
              />
            )}
          />

          <Box className="advanced-filter-grid">
            <TextField
              className="filter-control"
              type="date"
              label="From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{shrink: true}}
              variant="outlined"
              size="small"
            />
            <TextField
              className="filter-control"
              type="date"
              label="To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{shrink: true}}
              variant="outlined"
              size="small"
            />
          </Box>

          <Box className="advanced-filter-grid">
            <TextField
              className="filter-control"
              label="Ask min (R)"
              type="number"
              value={askMin}
              onChange={(e) =>
                setAskMin(e.target.value === '' ? '' : Number(e.target.value))
              }
              variant="outlined"
              size="small"
            />
            <TextField
              className="filter-control"
              label="Ask max (R)"
              type="number"
              value={askMax}
              onChange={(e) =>
                setAskMax(e.target.value === '' ? '' : Number(e.target.value))
              }
              variant="outlined"
              size="small"
            />
          </Box>

          <Box className="advanced-filter-grid">
            <TextField
              className="filter-control"
              label="Score min"
              type="number"
              value={scoreMin}
              onChange={(e) =>
                setScoreMin(e.target.value === '' ? '' : Number(e.target.value))
              }
              variant="outlined"
              size="small"
            />
            <TextField
              className="filter-control"
              label="Score max"
              type="number"
              value={scoreMax}
              onChange={(e) =>
                setScoreMax(e.target.value === '' ? '' : Number(e.target.value))
              }
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>

        <Box className="advanced-filter-actions">
          <Button onClick={clearAdvancedFilters}>Clear</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setAdvancedFiltersOpen(false)}
          >
            Apply filters
          </Button>
        </Box>
      </Drawer>

      {/* table */}
      <TableContainer component={Paper} className="table-container">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  cursor: 'pointer',
                  position: 'sticky',
                  left: 0,
                  background: '#fff',
                  zIndex: 2,
                  width: 110,
                  minWidth: 110,
                }}
                onClick={() => toggleSort('date')}
              >
                Date{arrow('date')}
              </TableCell>
              <TableCell
                style={{
                  position: 'sticky',
                  left: 110,
                  background: '#fff',
                  zIndex: 2,
                  width: 180,
                  minWidth: 180,
                }}
              >
                Company
              </TableCell>
              <TableCell>Province</TableCell>
              <TableCell>Applicant</TableCell>

              {/* sortable headers */}
              <TableCell
                style={{cursor: 'pointer'}}
                onClick={() => toggleSort('ask')}
              >
                Ask&nbsp;(R){arrow('ask')}
              </TableCell>
              <TableCell
                style={{cursor: 'pointer'}}
                onClick={() => toggleSort('score')}
              >
                Score{arrow('score')}
              </TableCell>
              <TableCell
                style={{cursor: 'pointer'}}
                onClick={() => toggleSort('ha')}
              >
                Ha{arrow('ha')}
              </TableCell>
              <TableCell>Crops</TableCell>
              <TableCell>Status</TableCell>
              <TableCell style={{width: 110, minWidth: 110}}>Owner</TableCell>
              <TableCell
                style={{cursor: 'pointer', width: 72, minWidth: 72}}
                onClick={() => toggleSort('done')}
              >
                Progress{arrow('done')}
              </TableCell>
              <TableCell style={{width: 40, minWidth: 40}} />
            </TableRow>
          </TableHead>

          <TableBody>
            {loading &&
              Array.from({length: 8}).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell>
                    <Skeleton variant="text" width={80} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={140} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={100} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={140} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={110} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={180} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rect" width={130} height={24} />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{gap: 8}}>
                      <Skeleton variant="circle" width={24} height={24} />
                      <Skeleton variant="circle" width={24} height={24} />
                      <Skeleton variant="rect" width={24} height={24} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={36} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rect" width={32} height={24} />
                  </TableCell>
                </TableRow>
              ))}
            {!loading &&
              filtered.map((a) => {
                const pct = donePct(a);

                return (
                  <TableRow
                    key={a.id}
                    className="clickable-row"
                    hover
                    onClick={() => {
                      NavigationService.navigate(`/applications/${a.id}`);
                    }}
                  >
                    <TableCell
                      style={{
                        position: 'sticky',
                        left: 0,
                        background: '#fff',
                        zIndex: 1,
                        width: 110,
                        minWidth: 110,
                      }}
                    >
                      {formatDate(a.createdAt, 'YYYY-MM-DD')}
                    </TableCell>
                    <TableCell
                      style={{
                        position: 'sticky',
                        left: 110,
                        background: '#fff',
                        zIndex: 1,
                        width: 180,
                        minWidth: 180,
                      }}
                    >
                      {isCompanyNameLoading(a) ? (
                        <Skeleton variant="text" width={140} />
                      ) : (
                        <Tooltip title={companyName(a)}>
                          <Typography noWrap variant="inherit">
                            {companyName(a)}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell style={{width: 40, minWidth: 40}}>
                      <Tooltip title={a.farm?.province || ''}>
                        <Typography noWrap variant="inherit">
                          {a.farm?.province || ''}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={farmer(a)}>
                        <Typography noWrap variant="inherit">
                          {farmer(a)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {_TotalFundingAsk(a).replace(/ /g, '\u00A0')}
                    </TableCell>
                    <TableCell>
                      {a.applyingUser?.creditScore !== undefined &&
                      a.applyingUser?.creditScore !== null &&
                      a.applyingUser?.creditScore >= 0
                        ? a.applyingUser.creditScore
                        : 'N/A'}
                    </TableCell>
                    <TableCell>{hectares(a)}</TableCell>
                    <TableCell>
                      <Tooltip title={cropList(a)}>
                        <Typography noWrap variant="inherit">
                          {truncate(cropList(a))}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <StatusDropdown
                        appId={a.id!}
                        value={a.applicationState as EApplicationStateType}
                      />
                    </TableCell>
                    {/* Ownership */}
                    <TableCell style={{width: 110, minWidth: 110}}>
                      {(() => {
                        const latest = getLatestOwnership(a);
                        return (
                          <OwnershipCell
                            appId={a.id!}
                            ownerId={ownerMap[a.id] || latest?.ownerId}
                            reportingToId={
                              reportingMap[a.id] || latest?.reportingToId
                            }
                            team={team}
                            saving={!!savingMap[a.id]}
                            onChange={(ownerId, reportingToId) => {
                              setOwnerMap((m) => ({...m, [a.id]: ownerId}));
                              setReportingMap((m) => ({
                                ...m,
                                [a.id]: reportingToId,
                              }));
                            }}
                            onSave={async (ownerId, reportingToId) => {
                              setSavingMap((s) => ({...s, [a.id]: true}));
                              try {
                                await apiCreateOrUpdateFDApplicationOwnership({
                                  applicationId: a.id!,
                                  ownerId,
                                  reportingToId,
                                });
                              } finally {
                                setSavingMap((s) => ({...s, [a.id]: false}));
                              }
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    {/* Progress */}
                    <TableCell style={{width: 72, minWidth: 72}}>
                      <ProgressCell pct={pct} />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        className="table-action-button"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          NavigationService.navigate(`/applications/${a.id}`);
                        }}
                      >
                        <KeyboardArrowRightIcon style={{fontSize: 20}} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Applications;
